import React from 'react';
import PropTypes from 'prop-types';
import { isArray, omitBy, isEqual } from 'lodash';

import Button from '../Button';
import RoundedPrimaryButton from '../RoundedPrimaryButton';
import RoundedGoBackButton from '../RoundedGoBackButton';

import withTranslations from '../../utils/Translations/withTranslations';
import translatePropTypes from '../../../translatePropTypes';
import Modal from '../../utils/Modal/Modal';
import SearchField from '../base/SearchField';

/* eslint-disable react/no-did-update-set-state */

@withTranslations
export default class TreeModalSelector extends React.PureComponent {
  static propTypes = {
    label: PropTypes.string.isRequired,
    models: PropTypes.array.isRequired,
    onChange: PropTypes.func,
    isFocusOnShow: PropTypes.bool,
    selection: PropTypes.oneOfType([
      PropTypes.any,
      PropTypes.arrayOf(PropTypes.any),
    ]),
    disabled: PropTypes.bool,
    required: PropTypes.bool,
    modalOpen: PropTypes.bool,
    errorMessage: PropTypes.string,
    children: PropTypes.func.isRequired,
    isOnOverlay: PropTypes.bool,
    applyLabel: PropTypes.string,
    isApplyDisabled: PropTypes.bool,
    searchField: PropTypes.bool,
    hasClearButton: PropTypes.bool,
    ...translatePropTypes,
  };

  static defaultProps = {
    disabled: false,
    required: false,
    isFocusOnShow: true,
    errorMessage: null,
    selection: null,
    modalOpen: false,
    isOnOverlay: false,
    onChange: null,
    applyLabel: 'Apply',
    isApplyDisabled: false,
    searchField: false,
    hasClearButton: true,
  };

  state = {
    selection: this.props.selection,
    searchText: '',
    searchModels: this.props.models,
    search: '',
  };
  componentDidUpdate(prevProps, prevState) {
    const differences = omitBy(this.props, (value, key) =>
      isEqual(value, prevProps[key]),
    );
    const { label } = this.props;
    if (this.props !== prevProps) {
      let models = [];
      const { searchField, selection } = this.props;
      if (searchField) {
        this.props.models.filter(model => {
          this.props.models.filter(singleModel => {
            if (model.id === singleModel.groupId) {
              models.push(model);
              models = [...new Set(models)];
            }
          });
          if (model.groupId) {
            models.push(model);
          }
        });
        this.modalList = models;
        if (prevState.searchModels !== this.modalList) {
          this.setState({ searchModels: this.modalList });
        }
      } else if (prevState.searchModels !== this.props.models) {
        this.setState({ searchModels: this.props.models });
      }
    }
    if (
      this.state.selection !== prevState.selection &&
      !isArray(this.state.selection)
    ) {
      this.handleApplyModal();
    }
  }
  modelsList = [];
  handleApplyModal = () => {
    const { onChange } = this.props;
    const { selection } = this.state;
    onChange && onChange(selection);
  };

  handleClear = () => this.handleSelectionChange([]);

  handleCancelModal = () => {
    const { onChange, selection } = this.props;
    onChange && onChange(selection, true);
  };

  handleSelectionChange = selection => {
    this.setState({ selection });
  };

  renderModalFooter = () => {
    const {
      t,
      isOnOverlay,
      applyLabel,
      isApplyDisabled,
      hasClearButton,
      direction,
      isLayoutRTL,
    } = this.props;
    const documentDir =
      typeof document !== 'undefined'
        ? document.documentElement?.getAttribute('dir')
        : null;
    const isRTLLayout =
      isLayoutRTL || direction === 'rtl' || documentDir === 'rtl';

    const cancelButton = isOnOverlay ? (
      <RoundedGoBackButton
        key="cancel"
        additionClasses="btn btn-default clickable mr-5"
        type="button"
        onClick={this.handleCancelModal}
      >
        {t('Go Back')}
      </RoundedGoBackButton>
    ) : (
      <Button key="cancel" buttonStyle="link" onClick={this.handleCancelModal}>
        {t('Cancel')}
      </Button>
    );

    const clearButton =
      hasClearButton && isArray(this.state.selection) ? (
        <Button key="clear" buttonStyle="link" onClick={this.handleClear}>
          {t('Clear')}
        </Button>
      ) : null;

    const applyButton = isArray(this.state.selection) ? (
      <RoundedPrimaryButton
        key="apply"
        disabled={isApplyDisabled}
        onClick={this.handleApplyModal}
      >
        {t(applyLabel)}
      </RoundedPrimaryButton>
    ) : null;

    return isRTLLayout ? (
      <>
        {applyButton}
        {clearButton}
        {cancelButton}
      </>
    ) : (
      <>
        {cancelButton}
        {clearButton}
        {applyButton}
      </>
    );
  };

  handleSearchQueryChange = searchText => {
    this.setState({ searchText });
  };

  focusSearchInput = ref => {
    const { isFocusOnShow } = this.props;

    if (!ref || !isFocusOnShow) {
      return;
    }

    const searchInputRef = ref.getElementsByTagName('input')[0];
    searchInputRef.focus();
  };

  handleSearchChange = search => {
    let filteredModels = [];
    this.setState({ search });

    if (search === '') {
      this.setState({ searchModels: this.modalList });
    } else {
      this.modalList.filter(model => {
        if (model.name.toLowerCase().includes(search.toLowerCase())) {
          filteredModels.push(model);
          this.modalList.filter(filtermodel => {
            if (model.groupId === filtermodel.id) {
              filteredModels.push(filtermodel);
              filteredModels = [...new Set(filteredModels)];
            }
          });
        }
      });
      this.setState({ searchModels: filteredModels });
    }
  };

  render() {
    const { label, children, modalOpen, direction, isLayoutRTL } = this.props;
    const { search, searchText, selection, searchModels } = this.state;
    const { searchField } = this.props;
    const documentDir =
      typeof document !== 'undefined'
        ? document.documentElement?.getAttribute('dir')
        : null;
    const isRTLLayout =
      isLayoutRTL || direction === 'rtl' || documentDir === 'rtl';
    return (
      <Modal
        renderFooter={this.renderModalFooter}
        title={label}
        visible={modalOpen}
        onClose={this.handleCancelModal}
      >
        <div className="row">
          <div className="col-xs-12">
            {searchField ? (
              <SearchField
                inputClassName={isRTLLayout ? 'text-right' : 'text-left'}
                inputProps={{
                  dir: isRTLLayout ? 'rtl' : 'ltr',
                }}
                placeholder="Search"
                value={search}
                widthClassName={
                  isRTLLayout
                    ? 'col-lg-4 col-md-4 col-sm-6 col-xs-12 pull-right'
                    : 'col-lg-4 col-md-4 col-sm-6 col-xs-12'
                }
                onChange={this.handleSearchChange}
              />
            ) : null}
            {children({
              models: searchModels,
              selection,
              searchText,
              onSelectionChange: this.handleSelectionChange,
              handleClear: this.handleClear,
            })}
          </div>
        </div>
      </Modal>
    );
  }
}
